/**
 * Tscheppaschlucht Bus-Schedule — pure data + lookup helpers.
 *
 * The JSON sources (timetables.json, locations.json) were extracted from
 * the earlier standalone bus-schedule webapp under
 * /Volumes/DatenAP/Code/linux/tscheppa. They describe the seasonal shuttle
 * bus that serves the gorge (line 5261, "Kärntner Linien") in pre- and
 * post-season only — during main summer season (~mid June to early
 * September) a different, denser schedule applies which is NOT covered
 * here yet.
 *
 * Runtime consumers:
 *   - TschaukoAgentRoot uses `getBusDigest()` to inject a fresh "next
 *     departures" block into the Realtime-agent system-prompt at
 *     session start (5-min horizon → ~90% of user questions answered
 *     from the model's head, no tool call).
 *   - Vite voice-tool plugin exposes `bus_next_departure` as a function
 *     tool for the remaining cases (specific stop, "and after that?").
 *
 * ALL times in this module are LOCAL AT time (Europe/Vienna). The bus
 * timetable is authored in local time; converting to UTC and back would
 * only introduce DST bugs.
 */

import timetablesRaw from './timetables.json'
import locationsRaw from './locations.json'

// ─── Types ─────────────────────────────────────────────────────────────

export type StopId =
  | 'bodental'
  | 'windisch'
  | 'windischp'
  | 'loibltal'
  | 'tscheppa'
  | 'unterloibl'
  | 'ferlach'
  | 'ferlach_ab'
  | 'weizelsdorf'

export type Direction = 'outbound' | 'return'

/** 'main' = Hauptsaison (29.6.–13.9. in 2026), dense schedule.
 *  'shoulder' = Vor- und Nachsaison, sparser schedule. */
export type SeasonPeriod = 'main' | 'shoulder'

export interface StopTime {
  stop: StopId
  /** "HH:MM" — empty string when the bus skips this stop. */
  time: string
}

export interface ScheduledTrip {
  id: string
  name: string
  direction: Direction
  season_period: SeasonPeriod
  schedule: StopTime[]
}

export interface Location {
  id: StopId
  name: string
  displayName: {
    text: string
    emoji: string
    category: string
    isDeparture?: boolean
  }
  lat: number
  lng: number
  description: string
}

export interface SeasonPeriodDef {
  id: SeasonPeriod
  name: string
  valid_from: Array<{ start: string; end: string }>
  source?: string
}

export interface Season {
  year: number
  periods: SeasonPeriodDef[]
}

export interface Timetables {
  season: Season
  travelTimes: Record<StopId, Partial<Record<StopId, number>>>
  connections: Array<{ from: StopId; to: StopId }>
  schedules: {
    weekday: ScheduledTrip[]
    weekend: ScheduledTrip[]
  }
}

const timetables = timetablesRaw as Timetables
const locations = (locationsRaw as { locations: Location[] }).locations

// ─── Public: static data ───────────────────────────────────────────────

export const SEASON: Season = timetables.season
export const LOCATIONS: Location[] = locations
export const TRAVEL_TIMES = timetables.travelTimes

/** Locations keyed by stop id — for O(1) lookup by name/emoji/coords. */
export const LOCATION_BY_ID: Record<string, Location> = Object.fromEntries(
  locations.map((l) => [l.id, l]),
)

// ─── Time utilities (local Europe/Vienna) ──────────────────────────────

/** Parse "HH:MM" as minutes-since-midnight. Empty/invalid → null. */
export function parseHM(hhmm: string): number | null {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null
  return h * 60 + min
}

/** Minutes-since-midnight → "HH:MM". */
export function formatHM(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * Which schedule-set applies on the given date?
 * - "weekend" on Sat/Sun (JS getDay() returns 0=Sun, 6=Sat).
 * - "weekday" otherwise.
 * Note: this ignores public holidays; the PDF treats them as weekend but
 * we can't detect them without a Kärntner holiday calendar. For an agent
 * conversation this is acceptable — we're honest about it in the digest.
 */
export function seasonTypeFor(date: Date): 'weekday' | 'weekend' {
  const d = date.getDay()
  return d === 0 || d === 6 ? 'weekend' : 'weekday'
}

/** Return the ISO date part (yyyy-mm-dd) of a LOCAL date. */
function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * Which timetable period applies on the given date?
 *   'main'     — Hauptsaison, dense schedule
 *   'shoulder' — Vor- oder Nachsaison, sparse schedule
 *   null       — outside every configured window (winter / off-season)
 *
 * Multiple non-overlapping windows per period are supported (shoulder has
 * both a pre-season and a post-season window).
 */
export function seasonPeriodFor(date: Date): SeasonPeriod | null {
  const iso = isoDate(date)
  for (const p of SEASON.periods) {
    for (const w of p.valid_from) {
      if (iso >= w.start && iso <= w.end) return p.id
    }
  }
  return null
}

/** Human-readable label for the current window (e.g. "Hauptsaison"). */
export function seasonPeriodName(date: Date): string {
  const id = seasonPeriodFor(date)
  return SEASON.periods.find((p) => p.id === id)?.name ?? 'außerhalb der Öffnungssaison'
}

/** Is `date` inside ANY configured period window (main OR shoulder)? */
export function isInSeasonWindow(date: Date): boolean {
  return seasonPeriodFor(date) !== null
}

// ─── Public: next-departures lookup ────────────────────────────────────

export interface DepartureQuery {
  /** Reference time — usually "now". */
  now: Date
  /** Which stop should the traveler board at? */
  from: StopId
  /** Optional destination — filters trips that actually serve `to`
   *  AFTER `from` in the same schedule direction. */
  to?: StopId | null
  /** How many upcoming departures to return. Default: 5. */
  count?: number
  /** Optional day-of-week override for testing. */
  seasonType?: 'weekday' | 'weekend'
}

export interface DepartureResult {
  /** Bus trip id (e.g. "outbound_1"). */
  tripId: string
  tripName: string
  direction: Direction
  /** "HH:MM" — departure at `from` stop. */
  departHM: string
  /** "HH:MM" — arrival at `to` if the query provided one, else null. */
  arriveHM: string | null
  /** Minutes from `now` to `departHM`. Negative-safe (0 when in the past). */
  minutesFromNow: number
  /** Minutes bus takes from `from` to `to`, null if `to` missing. */
  travelMinutes: number | null
  /** All stops of the trip with their times (skipped stops filtered out). */
  stops: StopTime[]
}

/**
 * Return the next N buses that depart from `from` after `now`.
 * When `to` is given, only trips that actually stop at `to` AFTER `from`
 * (same direction, same trip) are returned — filters out unrelated
 * routes and gives the traveler a real, boardable connection.
 *
 * Times are compared in the LOCAL day of `now`. Buses that already
 * departed today are excluded (no "next day" carryover here — the app
 * caller can advance `now` to tomorrow if the last bus has run).
 */
export function getNextDepartures(q: DepartureQuery): DepartureResult[] {
  const count = q.count ?? 5
  const seasonType = q.seasonType ?? seasonTypeFor(q.now)
  const period = seasonPeriodFor(q.now)
  // Off-season (winter): return empty — the caller/digest will explain.
  if (period == null) return []
  const trips = timetables.schedules[seasonType].filter(
    (t) => t.season_period === period,
  )

  const nowMin = q.now.getHours() * 60 + q.now.getMinutes()
  const results: DepartureResult[] = []

  for (const trip of trips) {
    const fromIdx = trip.schedule.findIndex((s) => s.stop === q.from && s.time)
    if (fromIdx < 0) continue
    const departMin = parseHM(trip.schedule[fromIdx].time)
    if (departMin == null || departMin < nowMin) continue

    let arriveHM: string | null = null
    let travelMinutes: number | null = null
    if (q.to) {
      // The `to` stop must come AFTER `from` in this trip's schedule.
      // Return-direction trips in the source data only carry the
      // ferlach_ab departure time — intermediate stops are listed with
      // empty times but the trip DOES serve them. So we accept a
      // no-time match here and fall back to the static travelTimes
      // table for an estimated arrival.
      const toEntry = trip.schedule.slice(fromIdx + 1).find((s) => s.stop === q.to)
      if (!toEntry) continue
      if (toEntry.time) {
        arriveHM = toEntry.time
        const arrMin = parseHM(toEntry.time)
        travelMinutes = arrMin != null ? arrMin - departMin : null
      } else {
        // Fallback estimate — reverse-lookup outbound travel time
        // (Ferlach → Tscheppa ≈ Tscheppa → Ferlach outbound = 5 min).
        // ferlach_ab is the departure-marker for Ferlach and shares
        // travel-time with ferlach for this estimate.
        const fromKey: StopId = q.from === 'ferlach_ab' ? 'ferlach' : q.from
        const estOutbound = TRAVEL_TIMES[q.to]?.[fromKey]
        if (typeof estOutbound === 'number') {
          travelMinutes = estOutbound
          arriveHM = formatHM(departMin + estOutbound)
        }
      }
    }

    results.push({
      tripId: trip.id,
      tripName: trip.name,
      direction: trip.direction,
      departHM: trip.schedule[fromIdx].time,
      arriveHM,
      minutesFromNow: departMin - nowMin,
      travelMinutes,
      stops: trip.schedule.filter((s) => s.time),
    })
  }

  results.sort((a, b) => a.minutesFromNow - b.minutesFromNow)
  return results.slice(0, count)
}

/**
 * Compact human-readable bus digest for injection into the Realtime
 * agent's system prompt at session-start. Covers the two most useful
 * direction combos:
 *   - "Aus der Schlucht": Tscheppa → Ferlach → Weizelsdorf (Bhf)
 *   - "In die Schlucht":  Ferlach → Tscheppa (return direction)
 *
 * Format is optimized for the model to read fast + speak naturally:
 *   Line 1: season note
 *   Line 2: "Nächste Busse Tscheppa → Ferlach: 13:03, 14:03, 15:03 …"
 *   Line 3: "Nächste Busse Ferlach → Tscheppa: 12:45, 13:45 …"
 *
 * Kept under ~250 tokens.
 */
export function getBusDigest(now: Date, count = 4): string {
  const seasonType = seasonTypeFor(now)
  const period = seasonPeriodFor(now)
  const dayLabel = seasonType === 'weekend' ? 'Samstag / Sonn- oder Feiertag' : 'Werktag (Mo–Fr)'
  const seasonNote = period
    ? `Heute (${dayLabel}) gilt der ${seasonPeriodName(now)}-Fahrplan ${SEASON.year} der Linie 5261 (Kärntner Linien).`
    : `Achtung: Heute liegt AUßERHALB der Öffnungssaison ${SEASON.year} (${SEASON.periods
        .flatMap((p) => p.valid_from.map((w) => `${w.start}–${w.end}`))
        .join(', ')}). Sag ehrlich, dass die Schlucht geschlossen ist und du keine Bus-Zeiten für heute hast.`

  const outFromGorge = getNextDepartures({
    now,
    from: 'tscheppa',
    to: 'ferlach',
    count,
    seasonType,
  })
  const returnToParkplatz = getNextDepartures({
    now,
    from: 'bodental',
    to: 'tscheppa',
    count,
    seasonType,
  })
  const intoGorge = getNextDepartures({
    now,
    from: 'ferlach_ab',
    to: 'tscheppa',
    count,
    seasonType,
  })

  const fmt = (r: DepartureResult): string =>
    r.arriveHM ? `${r.departHM} (an ${r.arriveHM})` : r.departHM

  const outLine = outFromGorge.length
    ? `Nächste Busse Tscheppaschlucht (Parkplatz) → Ferlach: ${outFromGorge.map(fmt).join(', ')}.`
    : 'Für heute keine weitere Abfahrt Tscheppaschlucht → Ferlach im hinterlegten Fahrplan.'
  // The "Rücktransport-für-Wanderer" case: they walk through and want the
  // bus AT Bodental to bring them back to the Parkplatz.
  const returnLine = returnToParkplatz.length
    ? `Rücktransport für Wanderer — nächste Busse Bodental (Gh Sereinig) → Tscheppaschlucht Parkplatz: ${returnToParkplatz.map(fmt).join(', ')}.`
    : 'Für heute kein weiterer Rücktransport Bodental → Tscheppaschlucht Parkplatz im hinterlegten Fahrplan.'
  const inLine = intoGorge.length
    ? `Nächste Busse Ferlach (Sparkassenplatz, Abfahrt) → Tscheppaschlucht: ${intoGorge.map(fmt).join(', ')}.`
    : 'Für heute keine weitere Abfahrt Ferlach → Tscheppaschlucht im hinterlegten Fahrplan.'

  return [
    `Aktuelle Zeit (Kärnten, lokal): ${formatHM(now.getHours() * 60 + now.getMinutes())} Uhr.`,
    seasonNote,
    outLine,
    returnLine,
    inLine,
    'Für andere Verbindungen (Loibltal Deutscher Peter als Einstieg, Weizelsdorf-Bhf, spätere Abfahrten) rufe bus_next_departure.',
  ].join(' ')
}
